/**
 * Contexte des filtres globaux (Site, Personne, Année, Mois, Trimestre).
 * Tous les widgets du cockpit se mettent à jour automatiquement lorsqu'un
 * filtre change, en consommant `filters` depuis ce contexte.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { dashboardApi } from "@/lib/api";

const FilterContext = createContext(null);

const DEFAULT_FILTERS = { site: "all", email: "all", year: "all", month: "all", quarter: "all" };

export function FilterProvider({ children }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [options, setOptions] = useState({ sites: [], persons: [], years: [], months: [], quarters: [] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [favoriteFilters, setFavoriteFilters] = useState(null);

  useEffect(() => {
    // Filtre "Personne" en cascade : ne propose que les personnes actives
    // sur le site sélectionné (si un site est sélectionné). Se déclenche
    // aussi au montage (site="all" par défaut -> liste complète).
    setOptionsLoading(true);
    dashboardApi.getFilterOptions({ site: filters.site }).then(({ data }) => {
      setOptions(data);
      // Si la personne actuellement sélectionnée n'existe plus dans la
      // nouvelle liste (site changé), on réinitialise plutôt que de garder
      // un filtre "Personne" invisible qui viderait silencieusement les
      // résultats.
      setFilters((prev) => {
        if (prev.email !== "all" && !data.persons.some((p) => p.email === prev.email)) {
          return { ...prev, email: "all" };
        }
        return prev;
      });
    }).catch(() => {}).finally(() => setOptionsLoading(false));
  }, [filters.site]);

  useEffect(() => {
    // Applique automatiquement la combinaison de filtres favorite de
    // l'utilisateur (si enregistrée) dès sa connexion.
    dashboardApi.getFavoriteFilters().then(({ data }) => {
      if (data.favorite_filters) {
        setFavoriteFilters(data.favorite_filters);
        setFilters((prev) => ({ ...prev, ...data.favorite_filters }));
      }
    }).catch(() => {});
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const saveFavoriteFilters = useCallback(async () => {
    const { data } = await dashboardApi.saveFavoriteFilters(filters);
    setFavoriteFilters(data.favorite_filters);
    return data.favorite_filters;
  }, [filters]);

  const clearFavoriteFilters = useCallback(async () => {
    await dashboardApi.deleteFavoriteFilters();
    setFavoriteFilters(null);
  }, []);

  return (
    <FilterContext.Provider
      value={{
        filters, updateFilter, resetFilters, options, optionsLoading, refresh, refreshKey,
        favoriteFilters, saveFavoriteFilters, clearFavoriteFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  return useContext(FilterContext);
}
